import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { DocAccessEffect, DocAccessSubjectType } from "@xop/dashboard-shared";

import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { PageLoader } from "@/components/ui/page-loader";
import { ApiError } from "../../../lib/api.js";
import {
  getAccessSubject,
  getAccessTree,
  saveAccessRules,
  setAccessMode,
  type DocAccessSubjectId,
} from "../api.js";
import { AccessTree } from "./AccessTree.js";
import { draftToPayload, resolveTree, ruleKey, type RuleDraft } from "./verdict.js";

/**
 * The documentation as ONE account sees it, with the control beside every
 * answer — the editing half of Documentation ▸ Access, extracted so it can
 * mount in two places (2026-09-14):
 *
 *   - `AccessPage`, next to the account rail (pick any customer or reseller);
 *   - the CRM reseller profile's **Documentation** tab, where the subject
 *     is the reseller the operator is already looking at, addressed as
 *     `crm-<id>` because that page knows only the crm-api id.
 *
 *   - the CRM customer profile's **Documentation** tab (2026-09-30), the
 *     same way: `organization` addressed as `crm-<crm customer id>`.
 *
 * Both are the same decision against the same rows through the same API;
 * two editors would be two places for the verdict logic to drift. The rail
 * stays out of here on purpose — the profile tab has no account to pick.
 *
 * Mount with a `key` that changes with the subject: the draft is local
 * state, and a draft that outlives a subject switch would land edits on
 * the wrong account.
 */
export function AccessEditor({
  subjectType,
  subjectId,
  notFound,
}: {
  subjectType: DocAccessSubjectType;
  subjectId: DocAccessSubjectId;
  /** What to show when portal-api has no such subject (a reseller not yet
   *  mirrored, a customer never synced). Defaults to a plain error. */
  notFound?: ReactNode;
}): JSX.Element {
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState<RuleDraft>(new Map());
  const [showHiddenOnly, setShowHiddenOnly] = useState(false);
  const [pendingStrict, setPendingStrict] = useState(false);

  const detailQuery = useQuery({
    queryKey: ["docs", "access", "subject", subjectType, subjectId],
    queryFn: () => getAccessSubject(subjectType, subjectId),
  });

  const treeQuery = useQuery({
    queryKey: ["docs", "access", "tree", subjectType, subjectId],
    queryFn: () => getAccessTree(subjectType, subjectId),
  });

  const detail = detailQuery.data?.data;
  const tree = treeQuery.data?.data;
  const mode = detail?.doc_access_mode ?? "auto";
  const portal = subjectType === "organization" ? "customer" : "reseller";
  // A reseller is blocked by default (2026-09-15): no Auto, no mode switch.
  const isReseller = subjectType === "reseller";

  const verdicts = useMemo(() => {
    if (!tree) return new Map();
    return resolveTree({ tree, draft, mode, portal });
  }, [tree, draft, mode, portal]);

  const pendingCount = useMemo(
    () => (tree ? draftToPayload(tree, draft).length : 0),
    [tree, draft],
  );

  const visibleNow = useMemo(() => {
    let count = 0;
    for (const verdict of verdicts.values()) if (verdict.visible) count += 1;
    return count;
  }, [verdicts]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!tree) throw new Error("Nothing to save");
      return saveAccessRules(subjectType, subjectId, draftToPayload(tree, draft));
    },
    onSuccess: () => {
      setDraft(new Map());
      void queryClient.invalidateQueries({ queryKey: ["docs", "access"] });
    },
  });

  const modeMutation = useMutation({
    mutationFn: (next: "auto" | "strict") => setAccessMode(subjectType, subjectId, next),
    onSuccess: () => {
      setPendingStrict(false);
      void queryClient.invalidateQueries({ queryKey: ["docs", "access"] });
    },
  });

  function handleSetRule(
    targetType: "Document" | "DocumentCategory",
    targetId: number,
    effect: DocAccessEffect | null,
  ): void {
    setDraft((previous) => {
      const next = new Map(previous);
      next.set(ruleKey(targetType, targetId), effect);
      return next;
    });
  }

  if (detailQuery.isLoading || treeQuery.isLoading) return <PageLoader />;

  if (!detail || !tree) {
    const missing = isNotFound(detailQuery.error) || isNotFound(treeQuery.error);
    if (missing && notFound) return <>{notFound}</>;
    return (
      <Alert severity="error" data-testid="access-editor-error">
        Could not load this account's documentation access.
      </Alert>
    );
  }

  return (
    <Stack spacing={2} data-testid="access-editor">
      <Card variant="outlined">
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }} noWrap>
              {detail.name}
            </Typography>
            <Chip
              size="small"
              label={`Sees ${visibleNow} of ${detail.counts.library}`}
              data-testid="access-visible-count"
            />
          </Box>

          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {detail.products.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                {subjectType === "reseller"
                  ? "No products yet — a reseller's products come from the customers assigned to it in the CRM."
                  : "No products recorded in the CRM for this customer."}
              </Typography>
            ) : (
              detail.products.map((product) => (
                <Chip
                  key={product.code}
                  size="small"
                  label={`${product.name} · ${product.code}`}
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11 }}
                />
              ))
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            {isReseller ? null : (
            <FormControlLabel
              control={
                <Switch
                  checked={mode === "strict"}
                  onChange={(e) => {
                    if (e.target.checked) setPendingStrict(true);
                    else modeMutation.mutate("auto");
                  }}
                  inputProps={{ "aria-label": "Strict whitelist" } as never}
                  data-testid="access-mode-switch"
                />
              }
              label={
                <Typography variant="body2">
                  {mode === "strict" ? "Strict whitelist" : "Automatic (by products)"}
                </Typography>
              }
            />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={showHiddenOnly}
                  onChange={(e) => setShowHiddenOnly(e.target.checked)}
                  data-testid="access-hidden-filter"
                />
              }
              label={<Typography variant="body2">Show hidden only</Typography>}
            />
          </Box>

          {isReseller ? (
            mode === "strict" ? (
              <Alert severity="info" sx={{ py: 0 }} data-testid="access-reseller-blocked-by-default">
                Blocked by default: {detail.name} reads nothing except what is allowed below — library
                folders and release guides alike. Block a document to keep it hidden inside an allowed folder.
              </Alert>
            ) : (
              // A reseller account from before 2026-09-15 that the access freeze has
              // not converted yet: it still reads by products. Say so, never pretend.
              <Alert severity="warning" sx={{ py: 0 }} data-testid="access-reseller-not-yet-frozen">
                {detail.name} still reads the documentation by products until its current access is
                converted to Allow rules (documents:freeze_reseller_access).
              </Alert>
            )
          ) : mode === "strict" ? (
            <Alert severity="warning" sx={{ py: 0 }}>
              This account sees nothing except what is explicitly allowed below.
            </Alert>
          ) : null}

          {!isReseller && (detail.reseller_rules_count ?? 0) > 0 ? (
            // The reseller layer (2026-09-30): the customer's reseller curated
            // some of this, bounded by what it can read. Staff rules still win.
            <Alert severity="info" sx={{ py: 0 }} data-testid="access-reseller-rules-note">
              {`Its reseller has shared or hidden ${pluralRules(detail.reseller_rules_count ?? 0)} for this customer (marked “Reseller” below). An Allow or Block of yours on the same row always wins.`}
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {pendingCount > 0 ? (
        <Box
          sx={{
            position: "sticky",
            top: 8,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 1.5,
            borderRadius: 1,
            border: 1,
            borderColor: "primary.main",
            bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
          }}
          data-testid="access-pending-bar"
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            {pendingCount} unsaved change{pendingCount === 1 ? "" : "s"}
          </Typography>
          <Button size="small" onClick={() => setDraft(new Map())}>
            Discard
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            data-testid="access-save"
          >
            {saveMutation.isPending ? "Saving…" : "Save access"}
          </Button>
        </Box>
      ) : null}

      {saveMutation.isError ? (
        <Alert severity="error">
          {(saveMutation.error as Error).message || "Could not save these changes."}
        </Alert>
      ) : null}

      <Card variant="outlined">
        <CardContent>
          <AccessTree
            tree={tree}
            verdicts={verdicts}
            draft={draft}
            onSetRule={handleSetRule}
            showHiddenOnly={showHiddenOnly}
            allowAuto={!isReseller}
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        isOpen={pendingStrict}
        onClose={() => setPendingStrict(false)}
        variant="warning"
        title="Switch to a strict whitelist?"
        description={`${detail.name} will immediately see NOTHING except the articles and folders explicitly allowed. Products and releases stop granting access.`}
        confirmLabel="Switch to whitelist"
        onConfirm={() => modeMutation.mutateAsync("strict").then(() => undefined)}
      />
    </Stack>
  );
}

function pluralRules(count: number): string {
  return count === 1 ? "1 document or folder" : `${count} documents or folders`;
}

/** portal-api answers an unknown subject with 404 `SUBJECT_NOT_FOUND` —
 * the only 404 these routes produce. */
function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}
